import * as React from 'react';

import {
  ACCOMMODATION_TYPES,
  CENTS_CONVERSION_TYPES,
  ENVIRONMENT,
} from '@src/shared/src/const/app';
import { formatCents } from '@src/shared/src/util/general';
import { t } from '@toolkit/util/i18n';
import { isEnvSearch } from '@src/util/app';
import { useSelector } from 'react-redux';
import { isAnyBookingItemAirbnb } from '@src/shared/src/selectors/checkoutSelectors';
import { Co2EmissionLabel } from '@src/components/Co2EmissionLabel';

type Props = {
  price: number;
  pricePerNight?: number;
  env: ENVIRONMENT;
  accommodationType: ACCOMMODATION_TYPES;
  currencyChanged?: boolean;
  negotiatedRates?: boolean;
  orgFavorited?: boolean;
  co2InKg?: number;
};

export const HotelPrice: React.FC<Props> = ({
  price,
  pricePerNight,
  env,
  accommodationType,
  currencyChanged,
  negotiatedRates,
  orgFavorited,
  co2InKg,
}) => {
  const bookingHasAirbnb = useSelector(isAnyBookingItemAirbnb);

  // Do not round off when in search
  const hotelPrice = formatCents(
    price,
    isEnvSearch(env) ? CENTS_CONVERSION_TYPES.FLOOR : CENTS_CONVERSION_TYPES.NONE,
  );
  const hotelPriceTooltip =
    accommodationType === ACCOMMODATION_TYPES.VACATION_RENTAL
      ? t('hotel-price.tooltip.vacation_rental')
      : null;
  const showPricePerNight = isEnvSearch(env) && pricePerNight;
  const showCurrencyChanged = !isEnvSearch(env) && currencyChanged && !bookingHasAirbnb;

  return (
    <>
      <div className="tcp-hotel-price">
        <div className="tcp-hotel-price-icons">
          {showCurrencyChanged && (
            <i
              data-rh={t('hotel.tooltip.body.text.differentCurrency')}
              data-rh-at="left"
              className="material-symbols">
              currency_exchange
            </i>
          )}
          {hotelPriceTooltip && (
            <i data-rh={hotelPriceTooltip} data-rh-at="left" className="material-symbols">
              info
            </i>
          )}
          {orgFavorited && (
            <i
              data-rh={t('hotel.tooltip.orgFavorite')}
              className="material-symbols tcp-hotel-favorite-icon">
              stars
            </i>
          )}
          {negotiatedRates && (
            <i
              data-rh={t('hotel.tooltip.overview.negotiatedRates')}
              className="material-symbols tcp-hotel-rate-icon">
              paid
            </i>
          )}
        </div>
        <span>{hotelPrice}</span>
      </div>
      {showPricePerNight && (
        <div className="tcp-hotel-price-per-night">
          {t('hotelPrice.label.pricePerNight', {
            price: formatCents(pricePerNight, CENTS_CONVERSION_TYPES.FLOOR),
          })}
        </div>
      )}
      {!!co2InKg && (
        <span data-rh={t('trip.tooltip.body.text.co2ValueDescription')} data-rh-at="left">
          <Co2EmissionLabel value={co2InKg} />
        </span>
      )}
    </>
  );
};
